import { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useAssignable, useAssignIssue } from '@/hooks/useIssueMemory';
import { apiErrorMessage, cn } from '@/utils/helpers';

/** Executives and PMs may assign open issues */
export function useCanAssign(open: boolean): boolean {
  const { user } = useAuth();
  return ['executive', 'pm'].includes(user?.role || '') && open;
}

/**
 * Assign an issue from the dashboard. The change goes to GitLab through the manager, as with
 * "@rv_manger assign"; people who cannot assign see the assignee as text.
 */
export function AssignPicker({
  gitlabProjectId,
  iid,
  assignees,
  open,
  className,
}: {
  gitlabProjectId: number;
  iid: number;
  assignees: string[];
  open: boolean;
  className?: string;
}) {
  const project = String(gitlabProjectId);
  const canAssign = useCanAssign(open);
  const { data: members, isLoading } = useAssignable(project, String(iid), canAssign);
  const assign = useAssignIssue(project, String(iid));
  const current = assignees[0] || '';
  // Shown until GitLab brings the new assignee back
  const [picked, setPicked] = useState<string | undefined>();
  const [note, setNote] = useState<string | null>(null);
  useEffect(() => setPicked(undefined), [current]);

  const names = assignees.length ? assignees.map((a) => `@${a}`).join(', ') : 'unassigned';
  if (!canAssign) return <span className={className}>{names}</span>;

  const change = async (username: string) => {
    setNote(null);
    setPicked(username);
    const ai = members?.find((m) => m.username === username)?.ai;
    try {
      const outcome = await assign.mutateAsync(username || null);
      if (outcome.decision !== 'executed') setPicked(undefined);
      setNote(
        outcome.decision === 'executed'
          ? ai
            ? 'Assigned. The AI developer checks that the issue is ready, then starts.'
            : username
              ? 'Assigned in GitLab.'
              : 'Unassigned in GitLab.'
          : outcome.decision === 'shadowed'
            ? 'Recorded only: this project is in shadow mode.'
            : `Not done: ${outcome.denyReason || outcome.decision}`
      );
    } catch (e) {
      setPicked(undefined);
      setNote(apiErrorMessage(e));
    }
  };

  const value = picked ?? current;
  return (
    <span className={cn('inline-flex flex-wrap items-center gap-2', className)}>
      <select
        className="h-8 rounded border bg-background px-2 text-sm text-foreground"
        value={value}
        disabled={isLoading || assign.isPending}
        onChange={(e) => change(e.target.value)}
        title="Assign in GitLab (through the manager)"
      >
        <option value="">Unassigned</option>
        {value && !members?.some((m) => m.username === value) && <option value={value}>@{value}</option>}
        {members?.map((m) => (
          <option key={m.username} value={m.username}>
            {m.ai ? `AI developer (@${m.username})` : `${m.name} (@${m.username})`}
          </option>
        ))}
      </select>
      {note && <span className="text-xs text-muted-foreground">{note}</span>}
    </span>
  );
}
